
import React from 'react';
import { fastingProtocols } from '@/data/fastingProtocols';
import { 
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFasting } from '@/contexts/FastingContext';
import { useNavigate } from 'react-router-dom';
import { FastingGraphic } from '@/components/FastingGraphic';
import { CircleAlert, CircleDot, CircleSlash, PanelTopClose, Timer, UtensilsCrossed } from 'lucide-react';
import { HalfCircle } from '@/components/icons/HalfCircle';

export const HomePage: React.FC = () => {
  const { selectProtocol, isActive, selectedProtocol } = useFasting();
  const navigate = useNavigate();

  const handleSelectProtocol = (protocol: any) => {
    selectProtocol(protocol);
    navigate('/timer');
  };

  const handleContinueFasting = () => {
    navigate('/timer');
  };

  const getDifficultyIcon = (hours: number) => {
    if (hours <= 16) {
      return <CircleDot className="text-green-500 ml-2" size={16} aria-label="Beginner" />;
    } else if (hours <= 24) {
      return <HalfCircle className="text-amber-500 ml-2" size={16} aria-label="Intermediate" />;
    } else if (hours <= 72) {
      return <CircleAlert className="text-orange-500 ml-2" size={16} aria-label="Advanced" />;
    } else {
      return <CircleSlash className="text-red-500 ml-2" size={16} aria-label="Expert" />;
    }
  };
  
  const getFeedingPeriod = (fastingHours: number) => {
    // For protocols over 24 hours, feeding period is 0
    if (fastingHours >= 24) {
      return 0;
    }
    // For standard intermittent fasting, feeding period is 24 - fasting hours
    return 24 - fastingHours;
  };

  return (
    <div className="container mx-auto p-4 max-w-md">
      <div className="mb-6 text-center slide-up">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">FastApp</h1>
        <p className="text-muted-foreground text-lg">
          {isActive 
            ? "Continue your ongoing fast" 
            : "Choose a fasting style to begin your health journey"}
        </p>
      </div>
      
      <section className="mb-8 prose prose-sm max-w-none">
        <h2 className="text-xl font-semibold mb-2 text-center">What is Intermittent Fasting?</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Intermittent fasting is an eating pattern that cycles between periods of eating and fasting. 
          It doesn't specify which foods to eat, but rather when you should eat them.
        </p>
        <div className="bg-fasttrack-light-blue/20 p-4 rounded-lg">
          <h3 className="text-md font-medium mb-2">Why Fast?</h3>
          <ul className="text-sm space-y-2 list-disc pl-4">
            <li><span className="font-medium">Weight Management</span>: Helps reduce calorie intake and improve hormone function</li>
            <li><span className="font-medium">Metabolic Health</span>: May improve insulin sensitivity and reduce inflammation</li>
            <li><span className="font-medium">Cancer Prevention</span>: Fasting may reduce cellular factors that promote cancer growth</li>
            <li><span className="font-medium">Heart Health</span>: May improve blood pressure, cholesterol and triglyceride levels</li>
            <li><span className="font-medium">Liver Function</span>: Helps reduce liver fat and promote cellular repair</li>
            <li><span className="font-medium">Brain Health</span>: May improve cognitive function and protect against neurodegenerative diseases</li>
          </ul>
        </div>
      </section>

      {isActive ? (
        <Card className="mb-8 glow-effect bg-gradient-to-br from-card to-primary/5 border-primary/20">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-3/4 p-6">
              <h3 className="text-xl font-semibold mb-2 text-primary">Ongoing Fast</h3>
              <p className="text-muted-foreground mb-4">You currently have an active {selectedProtocol.name} fast.</p>
              <Button 
                onClick={handleContinueFasting}
                size="lg" 
                variant="gradient"
                className="w-full md:w-auto mt-4"
              >
                Continue Fasting
              </Button>
            </div>
            <div className="relative bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center p-6 md:w-1/4">
              <FastingGraphic protocol={selectedProtocol} />
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-5 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-center bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">Choose Your Fasting Style</h2>
          {fastingProtocols.map((protocol, index) => (
            <Card key={protocol.id} className="overflow-hidden bg-gradient-to-br from-card to-accent/5 border-border/50 slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-2/3 p-5">
                  <div className="flex items-center mb-3">
                    <h3 className="text-lg font-semibold text-primary">{protocol.name}</h3>
                    {getDifficultyIcon(protocol.durationHours)}
                  </div>
                  <p className="text-muted-foreground my-3 text-sm leading-relaxed">{protocol.description}</p>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <div className="flex items-center bg-primary/10 rounded-lg p-2">
                      <Timer size={16} className="mr-2 text-primary" />
                      <span className="font-semibold text-primary">{protocol.durationHours} hours</span>
                      <span className="ml-1">fasting period</span>
                    </div>
                    
                    <div className="flex items-center bg-success/10 rounded-lg p-2">
                      <UtensilsCrossed size={16} className="mr-2 text-success" />
                      <span className={`font-semibold ${getFeedingPeriod(protocol.durationHours) === 0 ? 'text-destructive' : 'text-success'}`}>
                        {getFeedingPeriod(protocol.durationHours)} hours
                      </span>
                      <span className="ml-1">feeding period</span>
                    </div>
                  </div>
                </div>
                <div className="relative bg-gradient-to-br from-primary/10 to-accent/10 flex flex-col items-center justify-center p-4 md:w-1/3">
                  <div className="mb-4">
                    <FastingGraphic protocol={protocol} />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleSelectProtocol(protocol)}
                    variant="gradient"
                    className="w-full relative z-10 shadow-lg"
                  >
                    Select
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      <section className="mt-8 p-6 bg-gradient-to-br from-info/10 to-primary/10 rounded-xl border border-info/20 mb-8 slide-up">
        <h2 className="text-lg font-semibold mb-3 text-info">Getting Started with Fasting</h2>
        <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
          New to fasting? Start with the 16:8 style and gradually work your way up as your body adapts. 
          Remember to stay hydrated and listen to your body throughout your fasting journey.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          For optimal results, maintain a balanced diet during your eating windows and combine with regular physical activity.
        </p>
      </section>
      
      <footer className="mt-8 text-center text-xs text-muted-foreground">
        <p>Designed by <a href="https://wyattsanders.com" target="_blank" rel="noopener noreferrer" className="text-fasttrack-blue hover:underline">WyattSanders.com</a></p>
      </footer>
    </div>
  );
};
